import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, UserCircle, ChevronDown, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import logoLight from "@/assets/SRB.jpg";
import logoDark from "@/assets/SKD.jpg";
import { useAuth } from "@/contexts/AuthContext";
import { useRole, ROLE_LABELS, ALL_ROLES, type AppRole } from "@/contexts/RoleContext";
import { getV2NavForRole } from "@/v2/lib/v2Roles";
import { useBreadcrumb } from "@/components/layout/BreadcrumbContext";

export function V2Sidebar() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, isDevSession, profile } = useAuth();
  const { role, setRole } = useRole();
  const [roleOpen, setRoleOpen] = useState(false);

  const nav = getV2NavForRole(role);
  // Une page peut déclarer l'entrée de menu à surligner quand l'URL ne suffit
  // pas : la fiche d'un bien en VENTE vit sous /v2/biens/:id, partagée avec la
  // fiche location, et le surlignage par préfixe allumait « Catalogue
  // Location ». Cf. BreadcrumbDetail.activeNavPath.
  const { detail } = useBreadcrumb();
  const forcedNavPath = detail?.activeNavPath;
  const isRealAdmin = profile?.role === "admin" && !isDevSession;
  const isSimulating = isRealAdmin && role !== "admin";

  // V2 : sidebar TOUJOURS visible (même pour prestataire). En V1 elle était
  // masquée pour les prestataires car ils avaient une UI ultra-épurée — en V2
  // on garde la sidebar desktop pour la navigation + le retour vers V1 et la
  // bottom-nav mobile pour le terrain.

  return (
    <aside className="hidden md:flex w-64 bg-surface border-r border-border h-screen sticky top-0 overflow-y-auto flex-col flex-shrink-0">
      {/* Logo + badge V2 */}
      <div className="flex flex-col items-center pt-6 pb-4 gap-2">
        <img
          src={theme === "dark" ? logoDark : logoLight}
          alt="Skyline Renting"
          className="h-20 object-contain"
        />
        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[10px] font-semibold uppercase tracking-wider">
          <Sparkles size={10} /> Skyline OS — V2
        </div>
      </div>

      <nav className="flex-1 px-2 py-2">
        <div className="text-[10px] font-semibold tracking-widest uppercase text-text-muted px-3 pt-2 pb-2">
          {ROLE_LABELS[role]}
        </div>
        {nav.map((item, index) => {
          const Icon = item.icon;
          const isActive =
            !item.disabled &&
            (forcedNavPath
              ? forcedNavPath === item.to
              : location.pathname === item.to ||
                location.pathname.startsWith(item.to + "/"));
          const showSectionHeader =
            !!item.section && item.section !== nav[index - 1]?.section;
          return (
            <Fragment key={item.to}>
              {showSectionHeader && (
                <div className="text-[10px] font-semibold tracking-widest uppercase text-text-muted px-3 pt-3 pb-1.5">
                  {t(item.section!)}
                </div>
              )}
              <button
                type="button"
                onClick={() => !item.disabled && navigate(item.to)}
                disabled={item.disabled}
                title={item.disabled ? t("v2.sidebar.comingSoonTooltip") : undefined}
                className={cn(
                  "flex items-center gap-3 text-sm px-3 py-2 rounded-md transition-colors w-full text-left",
                  item.disabled
                    ? "text-text-muted/50 cursor-not-allowed"
                    : isActive
                      ? "text-accent bg-accent-light font-medium cursor-pointer"
                      : "text-text-secondary hover:bg-gray-100 hover:text-text-primary cursor-pointer",
                )}
              >
                <Icon size={18} className="shrink-0" />
                <span className="leading-none">{t(item.labelKey)}</span>
                {item.disabled && (
                  <span className="ml-auto text-[9px] uppercase tracking-wider bg-gray-100 text-text-muted px-1.5 py-0.5 rounded">
                    {t("v2.sidebar.comingSoon")}
                  </span>
                )}
              </button>
            </Fragment>
          );
        })}
      </nav>

      {/* Bas de sidebar : role switcher, logout. (Le bouton "Revenir à la V1"
          a été retiré — la V2 est désormais l'app par défaut.) */}
      <div className="border-t border-border py-2 px-2 flex flex-col gap-1">
        {isRealAdmin && (
          <div className="relative px-1">
            <button
              type="button"
              onClick={() => setRoleOpen((o) => !o)}
              className="w-full flex items-center gap-2 text-xs text-text-muted px-2 py-1.5 rounded-md hover:bg-gray-100 hover:text-text-primary cursor-pointer"
            >
              <UserCircle size={14} className="text-accent flex-shrink-0" />
              <span className="flex-1 text-left font-medium flex items-center gap-1.5">
                {ROLE_LABELS[role]}
                {isSimulating && (
                  <span className="flex items-center gap-1 text-[10px] font-normal text-warning">
                    <span className="w-1.5 h-1.5 rounded-full bg-warning" />
                    {t("v2.sidebar.simulation")}
                  </span>
                )}
              </span>
              <ChevronDown
                size={12}
                className={cn(
                  "transition-transform duration-150",
                  roleOpen && "rotate-180",
                )}
              />
            </button>
            {roleOpen && (
              <div className="absolute bottom-full left-1 right-1 mb-1 bg-surface border border-border rounded-md shadow-lg overflow-hidden z-50">
                {ALL_ROLES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRole(r as AppRole);
                      setRoleOpen(false);
                    }}
                    className={cn(
                      "w-full text-left px-3 py-1.5 text-xs transition-colors cursor-pointer",
                      r === role
                        ? "text-accent bg-accent-light font-medium"
                        : "text-text-secondary hover:bg-gray-100 hover:text-text-primary",
                    )}
                  >
                    {ROLE_LABELS[r as AppRole]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={async () => {
            await signOut();
            navigate("/login");
          }}
          className="flex items-center gap-3 text-sm text-text-secondary px-3 py-2 rounded-md hover:bg-gray-100 hover:text-text-primary cursor-pointer"
        >
          <LogOut size={18} />
          <span>{t("nav.deconnexion")}</span>
        </button>
      </div>
    </aside>
  );
}
